/**
 * UploadPrescriptionModal.js - Modal para subir receta y analizarla con IA
 * Incluye tips, validación de archivo y estados de carga
 */
import React, { useState, useRef } from 'react';

export default function UploadPrescriptionModal({
  show,
  onClose,
  onAnalyze,
  onFillManually,
  loading,
  error
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  if (!show) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de archivo no permitido. Solo se aceptan JPG, PNG y PDF.');
      return;
    }

    if (file.size > maxSize) {
      alert('El archivo es demasiado grande. El tamaño máximo es 10MB.');
      return;
    }

    setSelectedFile(file);
  };

  const handleAnalyze = () => {
    if (selectedFile) {
      onAnalyze(selectedFile);
    }
  };

  const getErrorDisplay = () => {
    if (!error) return null;

    const errorConfig = {
      wrong_document: {
        icon: '📄',
        title: 'Documento incorrecto',
        color: '#d97706',
        bgColor: '#fffbeb',
        showChangeFile: true,
        showFillManually: false
      },
      unrecognized_document: {
        icon: '❓',
        title: 'Documento no reconocido',
        color: '#d97706',
        bgColor: '#fffbeb',
        showChangeFile: true,
        showFillManually: true
      },
      factura: {
        icon: '⚠️',
        title: 'Documento no válido',
        color: '#f57c00',
        bgColor: '#fff3e0',
        showChangeFile: true,
        showFillManually: false
      },
      foto_random: {
        icon: '❌',
        title: 'No se detectó una receta',
        color: '#d32f2f',
        bgColor: '#ffebee',
        showChangeFile: true,
        showFillManually: true
      },
      ilegible: {
        icon: '⚠️',
        title: 'Imagen no legible',
        color: '#f57c00',
        bgColor: '#fff3e0',
        showChangeFile: true,
        showFillManually: false
      },
      receta_humana: {
        icon: '🐕',
        title: '¿Esta receta es tuya?',
        color: '#1976d2',
        bgColor: '#e3f2fd',
        showChangeFile: true,
        showFillManually: false
      },
      vision_api_not_enabled: {
        icon: '🔧',
        title: 'Servicio no disponible',
        color: '#7b1fa2',
        bgColor: '#f3e5f5',
        showChangeFile: false,
        showFillManually: true
      },
      quota_exceeded: {
        icon: '⏳',
        title: 'Limite alcanzado',
        color: '#f57c00',
        bgColor: '#fff3e0',
        showChangeFile: false,
        showFillManually: true
      },
      rate_limit: {
        icon: '⏳',
        title: 'Limite alcanzado',
        color: '#f57c00',
        bgColor: '#fff3e0',
        showChangeFile: false,
        showFillManually: true
      },
      server_error: {
        icon: '⚠️',
        title: 'Error del servidor',
        color: '#d32f2f',
        bgColor: '#ffebee',
        showChangeFile: false,
        showFillManually: true
      },
      file_too_large: {
        icon: '📦',
        title: 'Archivo muy grande',
        color: '#f57c00',
        bgColor: '#fff3e0',
        showChangeFile: true,
        showFillManually: false
      },
      invalid_file_type: {
        icon: '📄',
        title: 'Tipo de archivo no valido',
        color: '#f57c00',
        bgColor: '#fff3e0',
        showChangeFile: true,
        showFillManually: false
      },
      otro: {
        icon: '❌',
        title: 'Documento no reconocido',
        color: '#d32f2f',
        bgColor: '#ffebee',
        showChangeFile: true,
        showFillManually: true
      }
    };

    const config = errorConfig[error.errorType] || errorConfig.otro;

    return (
      <div style={{
        backgroundColor: config.bgColor,
        border: `1px solid ${config.color}`,
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>{config.icon}</span>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 8px 0', color: config.color, fontSize: '15px' }}>
              {config.title}
            </h4>
            <p style={{ margin: '0', color: '#555', fontSize: '14px' }}>
              {error.message}
            </p>
            {error.suggestions && error.suggestions.length > 0 && (
              <ul style={{ margin: '12px 0 0 0', paddingLeft: '20px', color: '#666', fontSize: '13px' }}>
                {error.suggestions.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            )}
            {/* Botones de acción según el tipo de error */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
              {config.showChangeFile && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: config.color,
                    border: `1px solid ${config.color}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '13px'
                  }}
                >
                  Cambiar archivo
                </button>
              )}
              {config.showFillManually && (
                <button
                  onClick={onFillManually}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: config.color,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '13px'
                  }}
                >
                  Llenar manualmente
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay upload-prescription-overlay" onClick={onClose}>
      <div
        className="modal-content upload-prescription-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Subir Receta para Análisis con IA</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ padding: '24px' }}>
          {/* Tips */}
          <div style={{
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#1565c0', fontSize: '14px' }}>
              📋 Tips para mejores resultados:
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#1565c0', fontSize: '13px' }}>
              <li>Receta impresa o digital</li>
              <li>Buena iluminación</li>
              <li>Imagen clara y enfocada</li>
              <li>Texto completo visible</li>
            </ul>
          </div>

          {/* Advertencia manuscrito */}
          <div style={{
            backgroundColor: '#fff8e1',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#f57c00'
          }}>
            ⚠️ <strong>Recetas manuscritas:</strong> La IA puede tener dificultad con letra de mano. Revisa con cuidado los datos extraídos.
          </div>

          {/* Error display */}
          {getErrorDisplay()}

          {/* Zona de carga de archivo */}
          {loading ? (
            <div style={{
              border: '2px dashed #2196f3',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: '#e3f2fd'
            }}>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #2196f3',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <p style={{ margin: 0, color: '#1565c0', fontWeight: '500' }}>
                Analizando receta...
              </p>
              <p style={{ margin: '8px 0 0', color: '#666', fontSize: '13px' }}>
                Esto puede tomar 5-10 segundos
              </p>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? '#4CAF50' : selectedFile ? '#4CAF50' : '#ccc'}`,
                borderRadius: '12px',
                padding: '40px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragOver ? '#e8f5e9' : selectedFile ? '#f1f8e9' : '#fafafa',
                transition: 'all 0.2s ease'
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {selectedFile ? (
                <>
                  <span style={{ fontSize: '40px' }}>✅</span>
                  <p style={{ margin: '12px 0 0', fontWeight: '500', color: '#2e7d32' }}>
                    {selectedFile.name}
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <p style={{ margin: '12px 0 0', color: '#888', fontSize: '12px' }}>
                    Click para cambiar archivo
                  </p>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '40px' }}>📎</span>
                  <p style={{ margin: '12px 0 0', fontWeight: '500', color: '#555' }}>
                    Seleccionar archivo
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>
                    o arrastra y suelta aquí
                  </p>
                  <p style={{ margin: '12px 0 0', color: '#aaa', fontSize: '12px' }}>
                    Formatos: PDF, JPG, PNG • Máx: 10MB
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderTop: '1px solid #eee'
        }}>
          <button
            onClick={onFillManually}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            Llenar manualmente
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleAnalyze}
              disabled={!selectedFile || loading}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: selectedFile && !loading ? '#4CAF50' : '#ccc',
                color: '#fff',
                cursor: selectedFile && !loading ? 'pointer' : 'not-allowed',
                fontWeight: '500'
              }}
            >
              Analizar con IA
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* ========================================
           BASE STYLES - Override Bootstrap
           ======================================== */
        .upload-prescription-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background-color: rgba(0, 0, 0, 0.5) !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          z-index: 1050 !important;
          padding: 20px !important;
        }

        .upload-prescription-modal {
          background: #fff !important;
          border-radius: 12px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          width: 100% !important;
          max-width: 520px !important;
          max-height: 90vh !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
          position: relative !important;
        }

        .upload-prescription-modal .modal-header {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          padding: 16px 24px !important;
          border-bottom: 1px solid #eee !important;
          flex-shrink: 0 !important;
          background: #fff !important;
        }

        .upload-prescription-modal .modal-header h3 {
          margin: 0 !important;
          font-size: 18px !important;
          font-weight: 600 !important;
          color: #333 !important;
        }

        .upload-prescription-modal .modal-close {
          background: none !important;
          border: none !important;
          font-size: 28px !important;
          cursor: pointer !important;
          color: #666 !important;
          padding: 0 !important;
          line-height: 1 !important;
          width: 36px !important;
          height: 36px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 50% !important;
          transition: background 0.2s !important;
        }

        .upload-prescription-modal .modal-close:hover {
          background: #f5f5f5 !important;
        }

        .upload-prescription-modal .modal-body {
          flex: 1 !important;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch !important;
        }

        .upload-prescription-modal .modal-footer {
          flex-shrink: 0 !important;
          background: #fff !important;
        }

        /* ========================================
           MOBILE FULLSCREEN (<768px)
           ======================================== */
        @media (max-width: 767px) {
          .upload-prescription-overlay {
            padding: 0 !important;
            align-items: stretch !important;
          }

          .upload-prescription-modal {
            max-width: 100% !important;
            width: 100% !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100vh !important;
            max-height: 100dvh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }

          .upload-prescription-modal .modal-header {
            padding: 16px 16px !important;
          }

          .upload-prescription-modal .modal-header h3 {
            font-size: 17px !important;
          }

          .upload-prescription-modal .modal-close {
            font-size: 32px !important;
            width: 44px !important;
            height: 44px !important;
            margin-right: -8px !important;
          }

          .upload-prescription-modal .modal-body {
            padding: 16px !important;
          }

          .upload-prescription-modal .modal-footer {
            flex-direction: column-reverse !important;
            gap: 12px !important;
            padding: 16px !important;
            border-top: 1px solid #eee !important;
          }

          .upload-prescription-modal .modal-footer > button,
          .upload-prescription-modal .modal-footer > div {
            width: 100% !important;
          }

          .upload-prescription-modal .modal-footer > div {
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
          }

          .upload-prescription-modal .modal-footer button {
            width: 100% !important;
            padding: 14px 20px !important;
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
}
